import {
  AppBar,
  type AppBarProps,
  Box,
  Link,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { sendSubmissionLinkEmail, useSubmission } from 'api/submission';
import CallUsIcon from 'assets/icons/call-us.svg';
import ChatBotIcon from 'assets/icons/chat-bot.svg';
import SaveIcon from 'assets/icons/save.svg';
import logoPrimary from 'assets/images/logo-primary.svg';
import { genericErrorMessage, META } from 'common/constants';
import { TertiaryButton } from 'components/lib/TertiaryButton';
import { SvgIconWrapper } from 'components/SvgIconWrapper';
import { displayToastMessage } from 'components/ToastMessage';
import { useFrontChat } from 'front-chat-sdk/hooks';
import { castArray } from 'lodash-es';
import theme from 'providers/theme/theme';
import { type FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useChatWidget } from 'store/features/chatWidget';
import { useFullscreenLoader } from 'store/features/UI';
import {
  HeaderAppBarStyle,
  HeaderLogoLinkStyle,
  HeaderLogoStyle,
  HeaderLogoWrapperStyle,
  HeaderQuoteNumberNumberStyle,
  HeaderQuoteNumberStyle,
  HeaderToolbarStyle,
} from './Header.styles';

type HeaderProps = {
  displayQuoteNumber?: boolean;
  displayPhoneNumber?: boolean;
  displaySaveAndExit?: boolean;
  displayChatWidget?: boolean;
} & AppBarProps;

const Header: FC<HeaderProps> = ({
  sx,
  displayQuoteNumber = false,
  displayPhoneNumber = true,
  displaySaveAndExit = false,
  displayChatWidget = false,
}) => {
  const { setFullscreenLoading } = useFullscreenLoader();
  const { frontChat } = useFrontChat();
  const { isChatWidgetWindowVisible, isInitialized } = useChatWidget();

  const isMobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletLayout = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('md'));

  const [disableCallUsLink, setDisableCallUsLink] = useState(false);

  const { id } = useParams();

  const { data: submission } = useSubmission(id as string, false);

  const handleSaveAndExit = async () => {
    if (!submission?.id) return;

    setFullscreenLoading(true, 'Saving');

    try {
      await sendSubmissionLinkEmail(submission?.id);

      window.location.href = META.HOME_LINK;
    } catch (_err) {
      displayToastMessage('ERROR', genericErrorMessage);
    } finally {
      setFullscreenLoading(false);
    }
  };

  const handleOpenChatWidget = () => {
    frontChat?.('show');
  };

  const renderQuoteNumber = () =>
    submission && displayQuoteNumber ? (
      <Stack
        sx={[
          HeaderQuoteNumberStyle,
          ...castArray({
            paddingLeft: displaySaveAndExit
              ? !isTabletLayout
                ? theme.spacing(2.5)
                : 0
              : !isTabletLayout
                ? theme.spacing(1.375)
                : 0,
          }),
        ]}
      >
        <Typography variant="h3" component="p">
          Quote Number
        </Typography>

        <Typography sx={HeaderQuoteNumberNumberStyle}>{submission?.quote_number}</Typography>
      </Stack>
    ) : null;

  useEffect(() => {
    if (!displayChatWidget || isDesktopLayout) {
      return undefined;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (isChatWidgetWindowVisible) {
      setDisableCallUsLink(true);
    } else {
      timeoutId = setTimeout(() => {
        setDisableCallUsLink(false);
      }, 1000); // slight delay to prevent flicker when closing chat and returning to header
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [displayChatWidget, isChatWidgetWindowVisible, isDesktopLayout]);

  const displayChatWidgetButton = displayChatWidget && !isDesktopLayout && isInitialized;

  return (
    <AppBar sx={[HeaderAppBarStyle, ...castArray(sx)]}>
      <Toolbar sx={HeaderToolbarStyle}>
        <Box sx={HeaderLogoWrapperStyle}>
          <Box component="img" src={logoPrimary} sx={HeaderLogoStyle} />
        </Box>

        {!isTabletLayout && renderQuoteNumber()}

        <Stack direction="row" gap={2}>
          {displayChatWidgetButton && (
            <TertiaryButton
              sx={[
                HeaderLogoLinkStyle,
                {
                  padding: 0,
                  width: 'unset',
                  flexDirection: isMobileLayout ? 'column' : 'row',
                  color: 'white',
                  minWidth: 'unset',
                  '&:hover': {
                    color: theme.palette.customColors.cream,
                  },
                },
              ]}
              startAdornment={<SvgIconWrapper src={ChatBotIcon} />}
              onClick={handleOpenChatWidget}
              text={
                <Typography variant="body2" sx={HeaderLogoLinkStyle} whiteSpace="nowrap">
                  Chat
                </Typography>
              }
            />
          )}

          {displaySaveAndExit && (
            <TertiaryButton
              sx={[
                HeaderLogoLinkStyle,
                { padding: 0, width: 'unset', flexDirection: isMobileLayout ? 'column' : 'row' },
              ]}
              startAdornment={<SvgIconWrapper src={SaveIcon} />}
              onClick={handleSaveAndExit}
              text={
                <Typography variant="body2" sx={HeaderLogoLinkStyle} whiteSpace="nowrap">
                  Save & exit
                </Typography>
              }
            />
          )}

          {displayPhoneNumber && (
            <Link
              tabIndex={-1}
              variant="body2"
              sx={{
                ...HeaderLogoLinkStyle,
                flexDirection: isMobileLayout ? 'column' : 'row',
                pointerEvents: disableCallUsLink ? 'none' : 'auto',
              }}
              href={`tel:${META.PRIORITY_CONTACT_NUMBER}`}
              target="_blank"
            >
              <SvgIconWrapper src={CallUsIcon} />

              <Typography whiteSpace="nowrap">
                {isMobileLayout ? 'Call us' : `Call ${META.PRIORITY_CONTACT_NUMBER}`}
              </Typography>
            </Link>
          )}
        </Stack>
      </Toolbar>

      {isTabletLayout && renderQuoteNumber()}
    </AppBar>
  );
};

export default Header;
